import React, { useState, useEffect } from "react";
import { useItems } from "../app/browsersSlice";
import { useCandidates } from "../app/candidatesSlice";
import { convert, asAmountComponent } from "../lib/misc";
import { groupBy, sum, min, max, get, cloneDeep } from "lodash";
import { format, startOfToday, addMonths, startOfMonth, addDays } from "date-fns";
import { Button, Common, SelectButton } from "@lainco/react-toolbox";
import { CashflowSidebar } from "../components/cashflows/CashflowSidebar";
import Filter, { ButtonOptionFilter, SelectOptionFilter, GroupByFilter } from "../components/v2/Filter";
import { buildOptions } from "../lib/misc";
import {
  mergeValues,
  buildCashflowsFromOutcomes,
  getScenarioLeafs,
  buildCashflowsFromScenarios,
  buildCashflowsFromMilestones,
  mergeCashflowsByPath,
  buildTreeFromCashflows,
  getVariablesByName,
  flattenTree,
} from "../cashflow/cashflows";
import { ScrollableTable } from "../components/ScrollableTable";
import { useFilter } from "./SellOrderMilestonesPage";
import styled from "styled-components";
import { outcomes, baseScenarios } from "./IncomesResumePage.data";

const useFilterOn = ({ title, name, state, filter, type, options, optionsLoading, fields }) => {
  const component =
    type === "button" ? (
      <ButtonOptionFilter header={title} name={name} options={options} optionsLoading={optionsLoading} />
    ) : type === "groupBy" ? (
      <GroupByFilter header={title} name={name} options={options} fields={fields} />
    ) : (
      <SelectOptionFilter header={title} name={name} options={options} optionsLoading={optionsLoading} />
    );
  return { state: { [name]: state }, filter, component };
};

const fieldProjectors = {
  category: (x) => x.sellOrder.category,
  project: (x) => x.sellOrder.project ?? { name: "NO PROJECT" },
  status: (x) => x.status,
  company: (x) => x.sellOrder.company,
  beneficiary: (x) => x.sellOrder.beneficiary,
  customer: (x) => x.sellOrder.customer,
  country: (x) => x.sellOrder.customer.country,
  useAccountManage: (x) => x.sellOrder.accountManager,
  projectLeader: (x) => x.sellOrder.projectLeader,
  currency: (x) => x.sellOrder.amount.currency,
  adjustmentClause: (x) => x.sellOrder.adjustmentClause,
};

const fields = [
  { code: "category", name: "Category" },
  { code: "project", name: "Product" },
  { code: "status", name: "Status" },
  { code: "company", name: "Company" },
  { code: "beneficiary", name: "Beneficiary" },
  { code: "customer", name: "Customer" },
  { code: "country", name: "Country" },
  { code: "accountManager", name: "Account Manager" },
  { code: "projectLeader", name: "Project Leader" },
  { code: "currency", name: "Currency" },
  { code: "adjustmentClause", name: "Adjustment Clause" },
];

const toMovement = (milestone, ars, usd, prices, getDate) => {
  const date = getDate(milestone);
  return {
    ...milestone,
    //sellOrder: { ...milestone.sellOrder, name: `[${milestone.sellOrder.id}] ${milestone.sellOrder.name}` },
    date: date,
    value: convert(date, milestone?.sellOrder?.amount?.currency, milestone?.getAmount(), ars, usd, prices),
    status: milestone.status,
  };
};

const getColumn = (date, status) =>
  startOfMonth(date).getTime() === startOfMonth(startOfToday()).getTime()
    ? {
        code: format(date, "yyyyMM") + "_" + (status === "settled" ? "settled" : "unsettled"),
        name: format(date, "MMM") + "'" + format(date, "yy") + " " + (status === "settled" ? " Settled" : " Pending"),
      }
    : { code: format(date, "yyyyMM"), name: format(date, "MMM") + "'" + format(date, "yy") };

const showOptionValues = [
  { id: "money", name: "Money" },
  { id: "people", name: "People" },
  { id: "both", name: "Both" },
];

export default function IncomesResumePage() {
  const start = startOfMonth(addDays(startOfToday(), -7)).getTime();
  const [items, itemsLoading] = useItems("sellOrderMilestones");
  const [prices] = useCandidates("prices");
  const [currencies] = useCandidates("currencies");
  const ranges = buildOptions(["name"], ["Current", "Historic"]);
  const dates = buildOptions(["name"], ["Exp. Ok", "Real Ok", "Settlement", "Settlment - 5"]);
  const [scenarios, setScenarios] = useState(baseScenarios);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showOptions, setShowOptions] = useState("money");
  const [accounts, accountsLoading] = useItems("bankAccounts");
  const [bank_balance, setBank_balance] = useState(0);
  const initialVariablesByName = getVariablesByName(baseScenarios);
  const [variablesByName, setVariablesByName] = useState(initialVariablesByName);

  const setVariable = (id, value) => {
    setVariablesByName((prev) => ({ ...prev, [id]: value }));
  };

  const ars = currencies?.find((c) => c.code === "ARS");
  const usd = currencies?.find((c) => c.code === "USD");
  const { initialState, filterComponents, filterFunction } = useFilter(
    useFilterOn({
      title: "Group By",
      state: { name: "Category, Country", value: ["category", "country"] },
      modal: <div />,
      name: "groupBy",
      filter: (filter, item) => true,
      fields: fields,
      type: "groupBy",
    }),
    useFilterOn({
      title: "Range",
      name: "range",
      state: 1,
      filter: (filter, item) => filter.range === 2 || getDate(item)?.getTime() >= start,
      type: "button",
      options: ranges,
      optionsLoading: false,
    }),
    useFilterOn({
      title: "By Date",
      name: "date",
      state: 3,
      filter: (filter, item) => true,
      type: "button",
      options: dates,
      optionsLoading: false,
    })
  );

  const [filterState, setFilterState] = useState(initialState);

  const formatAmountOptions = ({ isLeaf, level }) =>
    level === 0
      ? { colors: { zero: "white", positive: "green", negative: "red" } }
      : isLeaf
      ? { colors: { zero: "black", positive: "black", negative: "black" } }
      : { colors: { zero: "white", positive: "white", negative: "white" } };

  const getDate =
    filterState.date === 1
      ? // Expected Ok
        (item) => item.expectedOk
      : filterState.date === 2
      ? // Real Ok
        (item) => item.ok || item.expectedOk
      : filterState.date === 3
      ? // Settlement
        (item) => {
          const eta = item?.getExpectedSettlementDate();
          return eta == null || (eta.getTime() < startOfToday() && item.status !== "settled") ? startOfToday() : eta;
        }
      : // Settlement - 5
        (item) => {
          const eta = item?.getExpectedSettlementDate();
          const settlement = eta == null || (eta.getTime() < startOfToday() && item.status !== "settled") ? startOfToday() : eta;
          const ret = addDays(settlement, -5);
          return ret;
        };

  useEffect(() => {
    if (accounts == null) return;
    console.log({ accounts });
    setBank_balance(
      sum(
        accounts
          .filter((acc) => acc.currentBalance !== null)
          .map((acc) => (acc.currency.code === "USD" ? acc.currentBalance.balance : acc.currentBalance.balance / 1450))
      )
    );
  }, [accounts]);

  const loading = itemsLoading || prices == null || currencies == null;

  let table;
  let columns;

  if (!loading) {
    const filtered = items.filter((x) => x.status !== "canceled" && x.sellOrder?.project?.code !== "INT" && filterFunction(filterState, x));
    //.filter((x) => !(x.sellOrder.name === "The Brain - Phase IV - QUOT-1566 (979xSP)" && x.status !== "settled"));

    columns = [];
    const minDate = min(filtered.map((x) => startOfMonth(getDate(x))));
    const maxDate = max(filtered.map((x) => startOfMonth(getDate(x))));
    for (let d = minDate; d?.getTime() <= maxDate.getTime(); d = addMonths(d, 1)) {
      if (startOfMonth(d).getTime() === startOfMonth(startOfToday()).getTime()) {
        columns.push({
          key: getColumn(d, "settled").code,
          title: getColumn(d, "settled").name,
          align: "none",
          content: {
            projectors: (x) =>
              asAmountComponent(
                x.value?.[getColumn(d, "settled").code],
                x.people?.[getColumn(d, "settled").code],
                showOptions,
                formatAmountOptions(x)
              ),
          },
        });
        columns.push({
          key: getColumn(d, "pending").code,
          title: getColumn(d, "pending").name,
          align: "none",
          content: {
            projectors: (x) =>
              asAmountComponent(
                x.value?.[getColumn(d, "pending").code],
                x.people?.[getColumn(d, "pending").code],
                showOptions,
                formatAmountOptions(x)
              ),
          },
        });
      } else
        columns.push({
          key: getColumn(d, "settled").code,
          title: getColumn(d, "settled").name,
          align: "none",
          content: {
            projectors: (x) =>
              asAmountComponent(
                x.value?.[getColumn(d, "settled").code],
                x.people?.[getColumn(d, "settled").code],
                showOptions,
                formatAmountOptions(x)
              ),
          },
        });
      if (getColumn(d, "settled").code === "202612")
        columns.push({
          key: "2026_total",
          title: (
            <span
              style={{
                color: "white",
                background: "black",
                position: "absolute",
                left: 0,
                right: 0,
                top: 0,
                bottom: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: "1em",
              }}
            >
              Total 2026
            </span>
          ),
          align: "none",
          content: {
            projectors: (x) => (
              <div style={{ width: "8em" }}>
                <span
                  style={{
                    background: "black",
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1em",
                    borderTop: "1px solid #333",
                  }}
                >
                  {asAmountComponent(x.value?.["2026_total"], x.people?.["2026_total"], showOptions, {
                    colors: { zero: "white", positive: "white", negative: "white" },
                  })}
                </span>
              </div>
            ),
          },
        });
    }

    const outcomesCashflows = buildCashflowsFromOutcomes(outcomes);
    console.log({ outcomes, flattenOutcomes: flattenTree(outcomes), outcomesCashflows });
    const incomesMilestones = buildCashflowsFromMilestones(
      filtered.map((x) => toMovement(x, ars, usd, prices, getDate)),
      (m) =>
        [
          { id: (x) => "incomes", key: (g) => ({ name: "Incomes" }) },
          ...filterState.groupBy.value.map((f) => ({ id: (x) => x.id, key: (x) => fieldProjectors[f](x) })),
          { id: (x) => x.id, key: (x) => x.sellOrder },
        ].map((g) => g.key(m).name)
    );
    const baseScenariosCashflows = buildCashflowsFromScenarios(
      getScenarioLeafs(scenarios, (x) => x.isSelected),
      variablesByName
    );
    console.log({ scenarios, leafs: getScenarioLeafs(scenarios, (x) => x.isSelected), baseScenariosCashflows });
    const allCashflows = mergeCashflowsByPath([...incomesMilestones, ...outcomesCashflows, ...baseScenariosCashflows]);
    console.log({ allCashflows });
    const _tree = buildTreeFromCashflows(allCashflows, columns);
    console.log({ _tree });

    const buildTotalsRow = (field, rows, level) => {
      const value = Object.fromEntries(mergeValues(rows.filter((r) => r.level === level + 1).flatMap((r) => Object.entries(r.value))));
      const people = Object.fromEntries(
        mergeValues(rows.filter((r) => r.level === level + 1).flatMap((r) => Object.entries(r.people ?? {})))
      );
      return { key: field, value, people, isLeaf: false, level };
    };

    const treeToRows = (name, tree, level, totalOnTop = true) => {
      if (tree.money != null || tree.people != null)
        return [
          {
            key: name,
            value:
              tree.money != null
                ? Object.fromEntries([
                    ...tree.money,
                    ["_total", sum(tree.money.map((x) => x[1]))],
                    ["2026_total", sum(tree.money.filter((x) => x[0].startsWith("2026")).map((x) => x[1]))],
                  ])
                : null,
            people:
              tree.people != null
                ? Object.fromEntries([
                    ...tree.people,
                    ["_total", sum(tree.people.map((x) => x[1]))],
                    ["2026_total", sum(tree.people.filter((x) => x[0].startsWith("2026")).map((x) => x[1]))],
                  ])
                : null,
            isLeaf: true,
            level,
          },
        ];
      else {
        const rows = [];
        const keys = Object.entries(tree).sort((a, b) => (a[0] < b[0] ? -1 : 1));
        for (const [field, node] of keys) {
          const childRows = treeToRows(field, node, level + 1);
          rows.push(...childRows);
        }
        const total = buildTotalsRow(name, rows, level);
        return totalOnTop ? [total, ...rows] : [...rows, total];
      }
    };

    const _rows = treeToRows("Net", _tree, 0, false);
    const netValues = Object.entries(_rows[_rows.length - 1].value)
      .filter((x) => x[0] !== "2026_total")
      .sort((a, b) => (a[0] < b[0] ? -1 : 1));
    netValues[0][1] = bank_balance;
    for (var i = 1; i < netValues.length - 1; i++) {
      const prev = netValues[i - 1][1];
      netValues[i][1] = prev + (netValues[i][1] || 0);
    }
    netValues[netValues.length - 1][1] = null; // total
    const netPeopleValues = Object.entries(_rows[_rows.length - 1].people ?? {})
      .filter((x) => x[0] !== "2026_total")
      .sort((a, b) => (a[0] < b[0] ? -1 : 1));
    for (var i = 1; i < netPeopleValues.length - 1; i++) {
      const prev = Math.min(0, netPeopleValues[i - 1][1] + 2);
      netPeopleValues[i][1] = prev + (netPeopleValues[i][1] || 0);
    }
    netPeopleValues[netPeopleValues.length - 1][1] = null; // total

    _rows.push({
      key: "Balance",
      value: Object.fromEntries(netValues),
      people: Object.fromEntries(netPeopleValues),
      isLeaf: false,
      level: 0,
    });

    console.log({ _rows });

    table = (
      <TableContainer>
        <ScrollableTable
          data={_rows}
          columns={[
            {
              title: "Item",
              content: {
                projectors: (x) => x.key,
              },
            },
            ...columns.map((x) => ({ ...x })),
            {
              title: (
                <span
                  style={{
                    color: "white",
                    background: "black",
                    position: "absolute",
                    left: 0,
                    right: 0,
                    top: 0,
                    bottom: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "1em",
                  }}
                >
                  Total
                </span>
              ),
              align: "none",
              content: {
                projectors: (x) => (
                  <div style={{ width: "8em" }}>
                    <span
                      style={{
                        background: "black",
                        position: "absolute",
                        left: 0,
                        right: 0,
                        top: 0,
                        bottom: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        padding: "1em",
                        borderTop: "1px solid #333",
                      }}
                    >
                      {asAmountComponent(x.value._total, x.people?._total, showOptions, {
                        colors: { zero: "white", positive: "white", negative: "white" },
                      })}
                    </span>
                  </div>
                ),
              },
            },
          ]}
        />
      </TableContainer>
    );
  }

  const changeScenario = (path, value) => {
    var _scenarios = cloneDeep(scenarios);
    get(_scenarios, path).isSelected = value;
    setScenarios(_scenarios);
  };

  const changeOption = (path, index, value) => {
    var _scenarios = cloneDeep(scenarios);
    get(_scenarios, path).options[index].value = value;
    setScenarios(_scenarios);
  };

  return (
    <Common.Row cols="1fr auto">
      <Common.Col rows="auto 1fr" gap="1em" padding="1em">
        {/*
      <Modal title="Code" isOpen={isOpen} width="100%" onRequestClose={() => setIsOpen(false)}>
        <ModalHeader title="Edit Outcomes" onClosePressed={() => setIsOpen(false)} />
        <ModalBody>
          <CodeSandboxWorkerWithLint />
        </ModalBody>
        <ModalFooter>
          <Button type="secondary">Format</Button>
          <Button>Save</Button>
        </ModalFooter>
      </Modal>
      */}
        <Common.Row cols="1fr auto auto" gap="1em">
          <Title>Future Cashflow</Title>
          {/*<Filter fontSize="0.45em" model={filterState} onChange={setFilterState}>
            <div>
            <CodeButton onClick={() => setIsOpen(true)}>Edit Outcomes</CodeButton>
          </div>
            {filterComponents}
          </Filter>*/}
          <div style={{ zoom: 0.84 }}>
            <SelectButton type="buttons" value={showOptions} onChange={(e) => setShowOptions(e)} options={showOptionValues} />
          </div>
          <Button onClick={() => setSidebarOpen(!sidebarOpen)}>{sidebarOpen ? "Hide Sidebar" : "Show Sidebar"}</Button>
        </Common.Row>
        {loading ? "Loading..." : table}
      </Common.Col>
      {sidebarOpen && (
        <CashflowSidebar
          scenarios={scenarios}
          onScenarioChange={changeScenario}
          onOptionChange={changeOption}
          variablesByName={variablesByName}
          setVariable={setVariable}
        />
      )}
    </Common.Row>
  );
}

const Title = styled.div`
  font-size: 1.5em;
  font-weight: 600;
  color: #777;
`;

const CodeButton = styled(Button)`
  font-size: 1.1em;
`;

const TableContainer = styled.div`
  overflow-x: auto;

  @media print {
    overflow-x: none;
  }
`;
